import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ExternalLink, CheckCircle2, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { providers, providerDetails, providerOrder, company } from "../mock/mock";

const Provider = () => {
  const { slug } = useParams();
  const p = providers[slug];
  const d = providerDetails[slug];

  useEffect(() => {
    if (p) document.title = `${p.name} | Morrisville Communications`;
  }, [p]);

  if (!p) return <Navigate to="/" replace />;

  const others = providerOrder.filter((s) => s !== slug);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div
          className="absolute inset-0 opacity-30"
          style={{ background: `radial-gradient(circle at 70% 30%, ${p.color}, transparent 60%)` }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
          <span
            className="inline-flex items-center gap-2 text-xs font-bold px-4 py-1.5 rounded-full mb-6"
            style={{ backgroundColor: p.color, color: "#fff" }}
          >
            {p.badge}
          </span>
          <div
            className="text-5xl font-extrabold mb-6"
            style={{ color: p.color, filter: "brightness(1.4)" }}
          >
            {p.name}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold max-w-3xl leading-tight">{p.headline}</h1>
          <p className="mt-5 text-lg text-slate-300 max-w-2xl">{p.intro}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link to="/contact">
              <Button className="brand-gradient text-white rounded-full h-12 px-7 font-semibold">
                Find out more <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <a href={p.website} target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-full h-12 px-7 font-semibold border-white/40 text-white bg-white/10 hover:bg-white/20">
                Visit {p.name} <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* OVERVIEW + STATS */}
      {d && (
        <section className="py-16 bg-white border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-10 items-center">
            <div className="lg:col-span-2">
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-blue-700">Overview</span>
              <p className="mt-3 text-lg text-slate-600 leading-relaxed">{d.longIntro}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {d.stats.map((s) => (
                <div key={s.label} className="rounded-2xl border border-slate-100 bg-slate-50 p-5 text-center">
                  <div className="text-2xl font-extrabold" style={{ color: p.color }}>{s.value}</div>
                  <div className="text-xs text-slate-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HIGHLIGHTS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 text-center">Platform highlights</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {p.highlights.map((h) => (
              <Card key={h.title} className="card-hover p-8 rounded-2xl border-slate-100">
                <div
                  className="h-11 w-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${p.color}22` }}
                >
                  <CheckCircle2 className="h-5 w-5" style={{ color: p.color }} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{h.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{h.text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS & FEATURES */}
      {d && (
        <section className="py-20 brand-gradient-soft">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-blue-700">Products & features</span>
              <h2 className="text-3xl font-bold text-slate-900 mt-3">
                What you get with {p.name}
              </h2>
            </div>
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {d.products.map((pr) => (
                <Card key={pr.name} className="card-hover p-7 rounded-2xl border-slate-100 bg-white">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: p.color }} />
                    <h3 className="text-lg font-bold text-slate-900">{pr.name}</h3>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">{pr.text}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 brand-gradient-soft">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-slate-900">Want pricing & a demo of {p.name}?</h2>
          <p className="mt-4 text-slate-600 text-lg">
            We'll arrange a demo and partner pricing for {p.name} - at no cost and no obligation to you.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="brand-gradient text-white rounded-full h-12 px-8 font-semibold">
                Book a consultation <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <a href={`tel:${company.phoneTel}`}>
              <Button variant="outline" className="rounded-full h-12 px-8 font-semibold border-slate-300">
                <Phone className="h-4 w-4 mr-2" /> {company.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* OTHER PROVIDERS */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl font-bold text-slate-900 mb-6">Explore other providers</h3>
          <div className="flex flex-wrap gap-3">
            {others.map((s) => (
              <Link
                key={s}
                to={`/providers/${s}`}
                className="px-5 py-2.5 rounded-full border border-slate-200 text-slate-700 font-medium hover:border-blue-500 hover:text-blue-700 transition-colors"
              >
                {providers[s].name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Provider;
